<script setup lang="ts">
import { onKeyStroke, useScrollLock } from '@vueuse/core'
import { computed, ref, watch, nextTick } from '#imports'

export interface UISidePanelProps {
    side?: 'left' | 'right'
    width?: string
    background?: string
    closeOnBackdropClick?: boolean
    ariaLabel?: string
}

const props = withDefaults(defineProps<UISidePanelProps>(), {
    side: 'right',
    width: 'min(360px, 88vw)',
    background: undefined,
    closeOnBackdropClick: true,
    ariaLabel: undefined,
})

defineOptions({ inheritAttrs: false })

const panelStyle = computed(() => {
    const style: Record<string, string> = { width: props.width }
    if (props.background) style['--side-panel-bg'] = props.background
    return style
})

const modelValue = defineModel<boolean>({ default: false })

const emit = defineEmits<{
    open: []
    close: []
}>()

defineSlots<{
    header?: () => any
    default: () => any
    footer?: () => any
}>()

const panel = ref<HTMLElement | null>(null)
const previouslyFocused = ref<HTMLElement | null>(null)

const isLocked = import.meta.client ? useScrollLock(document.body) : ref(false)

function close() {
    if (!modelValue.value) return
    modelValue.value = false
}

function onBackdropClick() {
    if (!props.closeOnBackdropClick) return
    close()
}

onKeyStroke('Escape', () => {
    if (modelValue.value) close()
})

watch(modelValue, async (open) => {
    isLocked.value = open

    if (open) {
        previouslyFocused.value = (document.activeElement as HTMLElement | null) ?? null
        await nextTick()
        panel.value?.focus()
        emit('open')
    }
    else {
        previouslyFocused.value?.focus?.()
        previouslyFocused.value = null
        emit('close')
    }
})
</script>

<template>
    <ClientOnly>
        <Teleport to="#teleports">
            <Transition name="side-panel">
                <div
                    v-if="modelValue"
                    class="side-panel__backdrop"
                    role="presentation"
                    @click.self="onBackdropClick"
                >
                    <section
                        ref="panel"
                        class="side-panel"
                        :class="[
                            $attrs.class,
                            { 'side-panel--right': props.side === 'right', 'side-panel--left': props.side === 'left' },
                        ]"
                        :style="panelStyle"
                        role="dialog"
                        aria-modal="true"
                        :aria-label="props.ariaLabel"
                        tabindex="-1"
                        @click.stop
                    >
                        <header v-if="$slots.header" class="side-panel__header">
                            <slot name="header" />
                        </header>

                        <div class="side-panel__body">
                            <slot />
                        </div>

                        <footer v-if="$slots.footer" class="side-panel__footer">
                            <slot name="footer" />
                        </footer>
                    </section>
                </div>
            </Transition>
        </Teleport>
    </ClientOnly>
</template>

<style lang="scss" scoped>
.side-panel {
    --side-panel-bg: var(--surface, #fff);
    --side-panel-color: var(--on-surface, #111);

    position: fixed;
    top: 0;
    bottom: 0;
    z-index: 1;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr) auto;
    height: 100dvh;
    max-width: 100vw;
    background: var(--side-panel-bg);
    color: var(--side-panel-color);
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.18);
    overflow: hidden;
    outline: none;
    pointer-events: auto;

    &--right {
        right: 0;
    }

    &--left {
        left: 0;
        box-shadow: 8px 0 24px rgba(0, 0, 0, 0.18);
    }

    &__backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.45);
        z-index: z(dialog);
        isolation: isolate;
        pointer-events: auto;
    }

    &__header {
        min-height: fit-content;
    }

    &__body {
        overflow-y: auto;
        min-height: 0;
        -webkit-overflow-scrolling: touch;
    }

    &__footer {
        min-height: fit-content;
        border-top: 1px solid $panel-border-color;
    }
}

// Backdrop fade + panel slide
.side-panel-enter-active,
.side-panel-leave-active {
    transition: background-color 0.25s cubic-bezier(0.4, 0, 0.2, 1);

    .side-panel--right {
        transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .side-panel--left {
        transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    }
}

.side-panel-enter-from,
.side-panel-leave-to {
    background-color: rgba(0, 0, 0, 0);

    .side-panel--right {
        transform: translateX(100%);
    }

    .side-panel--left {
        transform: translateX(-100%);
    }
}
</style>
